import { createFileRoute, Link } from "@tanstack/react-router";
import { Callout, H3, H4 } from "@/components/site/InsuranceProse";
import type { ReactNode } from "react";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";

const FAQS: { q: string; a: ReactNode; schemaAnswer?: string }[] = [
  {
    q: "¿Medicare cubre Wegovy o Zepbound?",
    a: (
      <>
        {"Sí, por una de dos vías en 2027 — y su diagnóstico determina cuál, no su preferencia.\n\nA partir del 1 de enero de 2027, los planes de la Parte D deben incluir Wegovy, porque se convierte en un medicamento con precio negociado por Medicare."}
        <sup className="text-brand">
          <a
            href="#footnote-1"
            id="footnote-ref-1"
            aria-label="Nota 1"
            className="underline"
          >
            1
          </a>
        </sup>
        <br />
        <br />
        {"La Parte D lo cubre para reducir el riesgo cardiovascular en adultos con enfermedad cardiovascular establecida que también tienen sobrepeso u obesidad. Si tiene diabetes tipo 2, apnea obstructiva del sueño de moderada a grave o MASH no cirrótica con fibrosis de moderada a avanzada, también se le dirige a su plan habitual de la Parte D en lugar del Bridge, aunque el medicamento cubierto y la indicación pueden ser diferentes."}
        <br />
        <br />
        {"Si no tiene ninguna de esas condiciones, su vía es el Medicare GLP-1 Bridge."}
        <br />
        <br />
        {"El Bridge es un programa de demostración de CMS vigente hasta el 31 de diciembre de 2027 que cubre ciertos GLP-1 recetados para la reducción y el mantenimiento del peso con un copago mensual de $50. Cubre Wegovy inyectable, Wegovy en tabletas, Wegovy HD inyectable, Foundayo en tabletas y el KwikPen de Zepbound; otras presentaciones de Zepbound no están incluidas.\n\nCumplir los requisitos de una vía descarta la otra, y algunas personas no cumplen los requisitos de ninguna."}
      </>
    ),
    schemaAnswer:
      "Sí, por una de dos vías en 2027 — y su diagnóstico determina cuál, no su preferencia.\n\nA partir del 1 de enero de 2027, los planes de la Parte D deben incluir Wegovy, porque se convierte en un medicamento con precio negociado por Medicare. La Parte D lo cubre para reducir el riesgo cardiovascular en adultos con enfermedad cardiovascular establecida que también tienen sobrepeso u obesidad. Si tiene diabetes tipo 2, apnea obstructiva del sueño de moderada a grave o MASH no cirrótica con fibrosis de moderada a avanzada, también se le dirige a su plan habitual de la Parte D en lugar del Bridge, aunque el medicamento cubierto y la indicación pueden ser diferentes.\n\nSi no tiene ninguna de esas condiciones, su vía es el Medicare GLP-1 Bridge.\n\nEl Bridge es un programa de demostración de CMS vigente hasta el 31 de diciembre de 2027 que cubre ciertos GLP-1 recetados para la reducción y el mantenimiento del peso con un copago mensual de $50. Cubre Wegovy inyectable, Wegovy en tabletas, Wegovy HD inyectable, Foundayo en tabletas y el KwikPen de Zepbound; otras presentaciones de Zepbound no están incluidas.\n\nCumplir los requisitos de una vía descarta la otra, y algunas personas no cumplen los requisitos de ninguna.",
  },
  {
    q: "¿TRICARE exige probar antes otro medicamento para bajar de peso?",
    a: "Sí. TRICARE exige un ensayo de 3 meses con fentermina genérica, benzfetamina, dietilpropión o fendimetrazina que no haya logrado una pérdida del 5 por ciento del peso, a menos que usted tenga una contraindicación, haya tenido una reacción adversa, o haya perdido el 5 por ciento pero necesite reducir más. TRICARE también exige al menos 6 meses de modificación conductual y dietética documentada, y el prescriptor debe ser un proveedor de una instalación médica militar o de la red de TRICARE.",
  },
  {
    q: "¿Los planes de empleados federales (FEHB) cubren medicamentos para bajar de peso?",
    a: "Para el año de cobertura 2026, la Oficina de Administración de Personal (OPM) exigió que todos los planes FEHB cubran al menos un GLP-1 recetado para bajar de peso más al menos dos medicamentos orales adicionales contra la obesidad, así que existe algo de cobertura en todos los planes FEHB, aunque el medicamento preferido varía y se exige autorización previa. Para el año 2027, la OPM ha indicado a las aseguradoras que exigirá terapia conductual intensiva antes y durante la cobertura.",
  },
  {
    q: "¿Cómo empiezo a usar el Medicare GLP-1 Bridge?",
    a: "Primero se debe presentar y rechazar un reclamo de farmacia ante el procesador del Bridge, antes de que su proveedor envíe la autorización previa. Su proveedor presenta la solicitud; los pacientes no pueden hacerlo por su cuenta. Puede verificar su elegibilidad en Medicare.gov/glp1bridge o llamando al 1-800-MEDICARE.",
  },
  {
    q: "¿Qué pasa si tengo apnea del sueño y necesito Zepbound?",
    a: "TRICARE cubre Zepbound para la apnea obstructiva del sueño en adultos con un índice de apnea-hipopnea documentado de 15 o más eventos por hora y un IMC de 30 o más; Wegovy no está cubierto para esta indicación específica bajo TRICARE. Otros programas, como el Medicare GLP-1 Bridge, también reconocen la apnea del sueño como un diagnóstico que se procesa por una vía distinta.",
  },
];

export const Route = createFileRoute("/es/seguro/medicare-tricare-y-fehb")({
  head: () => {
    const base = esHead("insuranceMedicare");
    return {
      ...base,
      scripts: import.meta.env.SSR ? [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              esWebPageSchema("insuranceMedicare"),
              {
                "@type": "FAQPage",
                "@id": `${SITE}/es/seguro/medicare-tricare-y-fehb#faq`,
                inLanguage: "es-US",
                mainEntity: FAQS.map((f, i) => ({
                  "@type": "Question",
                  "@id": `${SITE}/es/seguro/medicare-tricare-y-fehb#faq-${i + 1}`,
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.schemaAnswer ?? (f.a as string) },
                })),
              },
            ],
          }),
        },
      ] : [],
    };
  },
  component: EsInsuranceMedicare,
});

function EsInsuranceMedicare() {
  return (
    <EsPageShell
      page="insuranceMedicare"
      title="Medicare, TRICARE y cobertura para empleados federales"
      intro="Los planes del gobierno siguen sus propias reglas, incluido el nuevo Medicare GLP-1 Bridge de 50 dólares y el requisito de terapia escalonada de TRICARE."
    >
      <EsSection id="empezar" heading="Antes de empezar">
        <Callout><strong>Información vigente al 27 de octubre de 2026.</strong></Callout>
        <p>
          Si aún no lo ha leído, el{" "}
          <Link to="/es/cobertura-de-seguro" className="font-medium text-brand-logo hover:underline">
            resumen general de cobertura de seguro
          </Link>{" "}
          explica los dos filtros y la vía alterna por diagnóstico en los que se apoyan mucho estos
          planes.
        </p>
      </EsSection>

      <EsSection id="medicare" heading="Medicare">
        <p>Hay dos vías separadas en 2027, y usted no puede elegir entre ellas. Su diagnóstico determina cuál le corresponde — y cumplir los requisitos de una descarta la otra.</p>

        <H3>Vía 1: Parte D, si tiene una condición que cumple los requisitos</H3>
        <p>A partir del 1 de enero de 2027, Wegovy se convierte en un medicamento con precio negociado por Medicare, con un precio justo máximo de $274 por mes, lo que significa que los planes de la Parte D deben incluirlo. Ozempic está en la misma lista al mismo precio.</p>
        <p>La Parte D cubre Wegovy para reducir el riesgo de infarto, accidente cerebrovascular y muerte cardiovascular en adultos con enfermedad cardiovascular establecida que también tienen sobrepeso u obesidad. Su plan determina su copago real, que no es lo mismo que el precio negociado de $274. Es probable que se requiera autorización previa y necesitará una farmacia de la red. A diferencia del Bridge, esta vía no tiene fecha de finalización.</p>
        <p>Se le dirige a la Parte D en lugar del Bridge si tiene apnea obstructiva del sueño de moderada a grave, MASH no cirrótica con fibrosis de moderada a avanzada, diabetes tipo 2 o enfermedad cardiovascular establecida para la cual el medicamento se receta para reducir el riesgo cardiovascular.</p>

        <H3>Vía 2: el Medicare GLP-1 Bridge, si no tiene una de esas condiciones</H3>
        <p>El Bridge es un programa de demostración de CMS vigente del 1 de julio de 2026 al 31 de diciembre de 2027. Cubre los GLP-1 recetados para la reducción y el mantenimiento del peso con un copago mensual de $50.</p>
        <p>Los medicamentos cubiertos son Wegovy inyectable, Wegovy en tabletas, Wegovy HD inyectable, Foundayo en tabletas y el KwikPen de Zepbound. Otras presentaciones de Zepbound no están incluidas — si usa Zepbound en vial o autoinyector, esto es importante.</p>
        <p>Debe tener 18 años o más y estar inscrito en un plan de la Parte D. Los $50 no cuentan para su deducible de la Parte D ni para su límite anual de gastos de bolsillo, y la asistencia del subsidio por bajos ingresos no se aplica a este copago.</p>
        <H4>El proceso inusual de autorización previa del programa Bridge</H4>
        <p>La forma en que funciona en la práctica es inusual y vale la pena conocerla de antemano. Su farmacia primero presenta un reclamo diseñado para ser rechazado. Solo después de ese rechazo puede su prescriptor presentar la autorización previa — usted no puede presentarla por su cuenta.</p>

        <p>El formulario registra su IMC cuando empezó el medicamento, en lugar de su IMC actual, junto con si tiene prediabetes, un infarto o accidente cerebrovascular previo, enfermedad arterial periférica sintomática, insuficiencia cardíaca con fracción de eyección preservada, presión arterial alta no controlada o enfermedad renal crónica en estadio 3a o superior.</p>

        <p>CMS no ha publicado qué combinaciones de IMC y condición realmente cumplen los requisitos. Verifique su propia elegibilidad en <a href="https://www.medicare.gov/glp1bridge" className="font-medium text-brand-logo hover:underline">Medicare.gov/glp1bridge</a> o llamando al <a href="tel:18006334227" className="font-medium text-brand-logo hover:underline">1-800-MEDICARE</a>.</p>

        <Callout>
          <strong>Algunas personas no cumplen los requisitos de ninguna de las dos vías.</strong>
          <p className="mt-3">Si su IMC está entre 27 y 30 y no tiene ninguna de las condiciones enumeradas, o si su IMC es de 35 o más pero no tiene enfermedad cardiovascular, puede quedar entre los dos programas.</p>
          <p className="mt-3">Si ese es su caso, vale la pena recibir una evaluación adecuada. La apnea del sueño, la prediabetes y el hígado graso sin diagnosticar son comunes y con frecuencia no se investigan, y un diagnóstico preciso puede cambiar qué vía está disponible para usted. Eso es buena medicina primero y una cuestión de cobertura después.</p>
        </Callout>

        <H3>Fechas</H3>
        <p>La inscripción abierta de Medicare va del 15 de octubre al 7 de diciembre de 2026, con cambios que entran en vigor el 1 de enero de 2027. Lea el Aviso Anual de Cambios de su plan para ver cómo maneja Wegovy, ya que los planes difieren en cómo aplican la autorización previa a los medicamentos con precios negociados.</p>
      </EsSection>

      <EsSection id="tricare" heading="TRICARE">
        <p>
          TRICARE es muy relevante en Virginia dada la comunidad militar de Hampton Roads y el norte
          de Virginia. Su criterio incluye un requisito de terapia escalonada que la mayoría de los
          planes comerciales no exige. Para adultos que buscan Wegovy o el Zepbound Pen Injector
          para el control de peso:
        </p>
        <ul className="list-disc space-y-1 pl-6">
          <li>
            IMC de 27 a 29 <strong>con</strong> al menos una condición relacionada con el peso, o IMC
            de 30 o más
          </li>
          <li>
            Certificación del proveedor de <strong>al menos 6 meses</strong> de modificación
            conductual y restricción dietética sin éxito, documentada en el expediente
          </li>
          <li>
            Un <strong>ensayo de 3 meses</strong> con fentermina genérica, benzfetamina,
            dietilpropión o fendimetrazina que no logró un 5 por ciento de pérdida de peso, a menos
            que exista una contraindicación, una reacción adversa, o ya se haya perdido el 5 por
            ciento pero se necesite reducir más
          </li>
          <li>
            No estar embarazada, no usar otro GLP-1, y no tener antecedentes personales ni
            familiares de cáncer medular de tiroides o síndrome MEN tipo 2
          </li>
        </ul>
        <p>
          <strong>El prescriptor debe ser un proveedor de una instalación médica militar o de la red
          de TRICARE</strong> que haya facturado a TRICARE por evaluarlo a usted y desarrollar un
          plan de tratamiento. Sin esto, la solicitud se rechaza automáticamente.
        </p>
        <p>
          <strong>De 12 a 17 años:</strong> solo está disponible la inyección de Wegovy, y se exige
          un IMC en el percentil 95 o superior. <strong>La aprobación dura 12 meses</strong>, con
          renovación anual que exige al menos un 5 por ciento de pérdida de peso desde el punto de
          partida (4 por ciento para los de 12 a 17 años). Para Zepbound y la apnea del sueño: adultos
          con un índice de apnea-hipopnea documentado de 15 o más eventos por hora y un IMC de 30 o
          más; Wegovy no está cubierto para esta indicación.
        </p>
      </EsSection>

      <EsSection id="fehb" heading="Empleados federales (FEHB y PSHB)">
        <p>La temporada abierta de inscripción para el año de cobertura 2027 va del 9 de noviembre al 14 de diciembre de 2026.</p>
        <p>Todas las aseguradoras de FEHB y PSHB están obligadas a cubrir al menos un medicamento GLP-1 contra la obesidad y al menos dos medicamentos orales contra la obesidad. Los medicamentos preferidos varían según el plan, y se exige autorización previa.</p>

        <H3>Qué cambia en 2027: el requisito de terapia</H3>
        <p>A partir del año de cobertura 2027, usted debe documentar su participación en un programa de terapia conductual intensiva basado en evidencia, tanto antes como durante el tratamiento con un medicamento contra la obesidad. Esto es nuevo, se aplica a todas las aseguradoras y no es opcional.</p>
        <p>Los planes deben cubrir de 12 a 22 sesiones de terapia conductual intensiva al año, con el número de sesiones según su progreso, y deben coordinar esa terapia con sus criterios para medicamentos y cirugía metabólica. Para niños y adolescentes de seis años o más con un IMC por encima del percentil 95, los planes deben cubrir programas que cumplan los requisitos, con al menos 26 horas de contacto durante un período de tres a doce meses.</p>

        <H3>Qué hacer al respecto</H3>
        <p>Si ya está tomando un GLP-1 a través de un plan FEHB, empiece a documentar la terapia conductual ahora en lugar de esperar hasta su próxima renovación. El requisito se aplica antes y durante el tratamiento, y una renovación presentada sin esa documentación es una negación previsible — una que le cuesta semanas cuando ya está a mitad del tratamiento.</p>
        <p>Si va a empezar en 2027, incorpore la terapia desde el principio. Las aseguradoras están obligadas a cubrirla, así que el verdadero costo es organizar las citas, no el dinero.</p>
        <p>Los criterios clínicos en sí no han cambiado mucho: la mayoría de las aseguradoras usa un IMC de 30, o de 27 con una condición relacionada con el peso, aunque cada una establece los suyos.</p>

        <Callout>
          <strong>Vale la pena comparar planes este año, aunque esté satisfecho con el suyo.</strong>
          <p className="mt-3">Las primas de los afiliados de FEHB suben aproximadamente un 10.9% para 2027, y varios planes FEHB y un plan PSHB dejarán de ofrecerse — los afiliados afectados deben elegir un nuevo plan o serán trasladados a un plan predeterminado.</p>
          <p className="mt-3">Como las aseguradoras difieren en el GLP-1 que prefieren y en cómo administran el nuevo requisito de terapia, el plan que fue mejor para usted en 2026 puede no serlo en 2027.</p>
        </Callout>
      </EsSection>

      <EsSection id="preguntas" heading="Preguntas frecuentes">
        <dl className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={f.q} id={`faq-${i + 1}`}>
              <dt className="font-medium text-brand-deep">{f.q}</dt>
              <dd className="mt-1">{f.a}</dd>
            </div>
          ))}
        </dl>
      </EsSection>

      <EsSection id="notas" heading="Notas">
        <ol className="list-decimal space-y-2 pl-6 text-sm text-foreground/70">
          <li id="footnote-1" className="scroll-mt-24">
            Medicare negociará el precio con los fabricantes de los medicamentos, por $274 o menos.
            Su copago debería ser mucho menor que esa cifra.{" "}
            <a
              href="#footnote-ref-1"
              className="text-brand-deep underline hover:no-underline"
              aria-label="Volver al texto"
            >
              ↩
            </a>
          </li>
        </ol>
      </EsSection>

      <EsSection id="mas" heading="Más guías de cobertura">
        <p>
          Consulte también la cobertura de{" "}
          <Link
            to="/es/seguro/plan-comercial-y-marketplace"
            className="font-medium text-brand-logo hover:underline"
          >
            planes comerciales y del Marketplace
          </Link>
          , de{" "}
          <Link
            to="/es/seguro/medicaid-y-cardinal-care"
            className="font-medium text-brand-logo hover:underline"
          >
            Medicaid y Cardinal Care
          </Link>
          , o vuelva al{" "}
          <Link to="/es/cobertura-de-seguro" className="font-medium text-brand-logo hover:underline">
            resumen general de cobertura
          </Link>
          . Si tiene preguntas específicas sobre su caso, {" "}
          <Link to="/es/contacto" className="font-medium text-brand-logo hover:underline">
            contáctenos
          </Link>
          .
        </p>
      </EsSection>
    </EsPageShell>
  );
}
